import Vue from 'vue';
import { AlertEdit, Alert } from '@/interfaces/alerts';
import { Commit } from 'vuex';

interface AlertState {
	alerts: {
		[id: number]: Alert;
	};
	customAlerts: {
		[id: number]: Alert;
	};
	selectedAlert: Alert | null;
}

interface AlertParams {
	ack: boolean;
	active: boolean;
	start_date: string;
	end_date: string;
}

export default {
	namespaced: true,
	state: {
		alerts: [],
		customAlerts: [],
		selectedAlert: null,
	} as AlertState,
	getters: {
		getAlerts: (state: AlertState): Alert[] => {
			return Object.values(state.alerts);
		},

		getActiveAlerts: (state: AlertState): Alert[] => {
			return Object.values(state.alerts).filter((alert: Alert) => alert.active);
		},

		getDeviceActiveAlerts:
			(state: AlertState) =>
			(deviceId: number): Alert[] => {
				return Object.values(state.alerts).filter(
					(alert: Alert) => alert.active && alert.fk_device === deviceId
				);
			},

		getCustomAlerts: (state: AlertState): Alert[] => {
			return Object.values(state.customAlerts);
		},

		getAlert:
			(state: AlertState) =>
			(id: number): Alert | undefined => {
				return state.alerts[id];
			},

		getSelectedAlert: (state: AlertState): Alert | null => {
			return state.selectedAlert;
		},
	},
	mutations: {
		SET_ALERTS(state: AlertState, alerts: Alert[]): void {
			const ret: { [id: number]: Alert } = {};
			state.alerts = alerts.reduce((obj, alert) => {
				obj[alert.id] = alert;
				return obj;
			}, ret);
		},

		SET_CUSTOM_ALERTS(state: AlertState, alerts: Alert[]): void {
			const ret: { [id: number]: Alert } = {};
			state.customAlerts = alerts.reduce((obj, alert) => {
				obj[alert.id] = alert;
				return obj;
			}, ret);
		},

		ADD_ALERT(state: AlertState, alert: Alert): void {
			Vue.set(state.alerts, alert.id, alert);
		},

		CHANGE_ALERT(state: AlertState, alert: Alert & { id: number }): void {
			const oldAlert = state.alerts[alert.id] || {};
			Vue.set(state.alerts, alert.id, { ...oldAlert, ...alert });
		},

		CHANGE_SELECTED_ALERT(state: AlertState, alert: Alert): void {
			state.selectedAlert = alert;
		},

		REMOVE_ALERT(state: AlertState, id: number): void {
			Vue.delete(state.alerts, id);
		},

		CLEAN_ALERT(state: AlertState): void {
			state.alerts = [];
		},

		CLEAN_CUSTOM_ALERT(state: AlertState): void {
			state.customAlerts = [];
		},

		CLEAN_SELECTED_ALERT(state: AlertState): void {
			state.selectedAlert = null;
		},
	},
	actions: {
		async fetchAlerts({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/alerts`, {
				params: { active: true },
			});

			if (response && response.status === 200) {
				commit('SET_ALERTS', response.data.data);
			}
		},

		async fetchCustomAlerts(
			{ commit }: { commit: Commit },
			{ params }: { params: AlertParams }
		): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/alerts`, {
				params: params,
			});

			if (response && response.status === 200) {
				commit('SET_CUSTOM_ALERTS', response.data.data);
			}
		},

		async addAlert({ commit }: { commit: Commit }, { alert }: { alert: AlertEdit }): Promise<void> {
			const response = await Vue.axios.post(`${process.env.VUE_APP_API_URL}/alerts`, alert);
			if (response && response.status === 200) {
				commit('ADD_ALERT', response.data.data);
			}
		},

		async modifyAlert(
			{ commit }: { commit: Commit },
			{ id, alert }: { id: number; alert: AlertEdit }
		): Promise<void> {
			const response = await Vue.axios.put(`${process.env.VUE_APP_API_URL}/alerts/${id}`, alert);
			if (response && response.status === 200) {
				commit('CHANGE_ALERT', response.data.data);
			}
		},

		selectAlert({ commit }: { commit: Commit }, { alert }: { alert: Alert }): void {
			if (alert) {
				commit('CHANGE_SELECTED_ALERT', alert);
			}
		},

		removeAlertLocally({ commit }: { commit: Commit }, { id }: { id: number }): void {
			if (id) {
				commit('REMOVE_ALERT', id);
			}
		},

		cleanAlert({ commit }: { commit: Commit }): void {
			commit('CLEAN_ALERT');
		},

		cleanCustomAlert({ commit }: { commit: Commit }): void {
			commit('CLEAN_CUSTOM_ALERT');
		},

		cleanSelectedAlert({ commit }: { commit: Commit }): void {
			commit('CLEAN_SELECTED_ALERT');
		},

		cleanAllAlert({ commit }: { commit: Commit }): void {
			commit('CLEAN_ALERT');
			commit('CLEAN_CUSTOM_ALERT');
			commit('CLEAN_SELECTED_ALERT');
		},
	},
};
